import { alpha, createTheme, type Components } from '@mui/material/styles'
import typography, { fonts } from './typography'
import primitives from './primitives'
import { borderRadius } from './borderRadius/types'
import { elevation } from './elevation/types'
import { spacing } from './spacing'
import { breakpoints } from './breakpoint'
import zIndexes from './zIndex'
import { createLegacyPaletteAliases, createMuiPaletteBridge, type ThemeMode } from './createPaletteBridge'
import { darkTokens } from './tokens/generated/dark'
import { lightTokens } from './tokens/generated/light'
import type { SemanticTokens } from './tokens/generated/types'
import type { Theme } from './themeTypes'

const createComponents = (tokens: SemanticTokens): Components => ({
  MuiCssBaseline: {
    styleOverrides: `
    ${fonts.graphikRegular}
    ${fonts.graphikMedium}
    ${fonts.firaCodeRegular}
    ${fonts.firaCodeMedium}
    `
  },
  MuiBackdrop: {
    styleOverrides: {
      root: {
        backgroundColor: alpha(tokens.background.tonal.medium.pressed, 0.5)
      },
      // Popover/Menu/Select use invisible backdrops for click-outside — keep them transparent.
      invisible: {
        backgroundColor: 'transparent'
      }
    }
  },
  MuiButtonBase: {
    styleOverrides: {
      root: {
        '& .MuiTouchRipple-root .MuiTouchRipple-child': {
          backgroundColor: 'transparent',
          borderRadius: '16px'
        }
      }
    }
  },
  MuiListItemButton: {
    styleOverrides: {
      root: {
        borderRadius: '16px',
        '&:hover': {
          backgroundColor: tokens.background.tonal.superSoft.hover
        }
      }
    }
  }
})

export const createKuramaTheme = (mode: ThemeMode): Theme => {
  const tokens = mode === 'dark' ? darkTokens : lightTokens
  const legacyPalette = createLegacyPaletteAliases(primitives)
  const muiPalette = createMuiPaletteBridge(mode, primitives)

  return createTheme({
    mode,
    primitives,
    tokens,
    palette: {
      ...legacyPalette,
      ...muiPalette
    },
    typography,
    borderRadius,
    spacing,
    elevation,
    components: createComponents(tokens),
    breakpoints,
    zIndex: zIndexes
  })
}

export const lightTheme = createKuramaTheme('light')
export const darkTheme = createKuramaTheme('dark')

/** @deprecated Use `lightTheme`, `darkTheme`, or `useTheme()` instead. */
export const defaultTheme = lightTheme

export default defaultTheme
